@use '@/styles/tokens' as *;

// Figma nodes 219:327388 / 219:334140 — a 56px dark pill with the 74px power disc overlapping its
// centre, and a red progress ring around the disc while a stop timer runs.

$disc-size: 74px;
$pill-height: 56px;
// Headroom around the silhouette for the blur `#device-cluster-goo` applies. Must comfortably
// exceed the filter's stdDeviation or the fillets get clipped at the layer's edge.
$goo-pad: 24px;

.cluster {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
}

// The pill and the disc that overlaps it. Kept as its own layer so the §9 queued tag can sit
// below the pair in normal flow instead of fighting the disc's absolute centring.
.stack {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/**
 * The pill's silhouette, and the disc's, merged into one shape.
 *
 * In the design (219:340859) the pill's edges sweep *outward* as they near the disc and meet it
 * tangentially — the two are one continuous outline, not a circle laid on a rectangle. Drawing
 * them as separate boxes leaves the disc's edge visibly cutting across the pill's straight edge,
 * which is the join the design does not have.
 *
 * The fillet is produced by blurring both shapes together and then thresholding the result back to
 * a hard edge (`#device-cluster-goo`): where the two overlap, the summed blur crosses the
 * threshold slightly outside their union and resolves to a smooth tangent curve. That is the whole
 * reason for the filter — it is not decoration, and it is what lets the pill keep a width driven by
 * its labels. A fixed SVG path could not follow that, and a fillet built from background gradients
 * cannot span a box whose width is unknown.
 *
 * Only these two colour blocks are filtered. The labels, icons and power button render above, in
 * `.pill` and `.disc`, so no text is ever blurred.
 */
// The filter lives in the DOM but must never take layout or be read out.
.gooDef {
  position: absolute;
  width: 0;
  height: 0;
}

.backdrop {
  position: absolute;
  inset: -$goo-pad 0;
  filter: url('#device-cluster-goo');
  pointer-events: none;
}

.backdropPill {
  position: absolute;
  // Back to the pill's own box, undoing the padding its parent added for the blur.
  inset: $goo-pad 0;
  border-radius: $radius-pill;
  background: $color-neutral-950;
}

.backdropDisc {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  // Drawn a little over `$disc-size`: thresholding the blur at alpha 0.5 erodes a convex edge by
  // roughly a pixel, so the silhouette lands on the intended 74px only if the source is wider.
  width: $disc-size + 3px;
  height: $disc-size + 3px;
  border-radius: 50%;
  background: $color-neutral-950;
}

.pill {
  // Positioned so it paints above `.backdrop`: an absolutely-positioned sibling would otherwise
  // sit on top of a static one regardless of DOM order, hiding the labels behind the silhouette.
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  height: $pill-height;
  padding-inline: var(--space-2);
  border-radius: $radius-pill;
  // The fill now comes from `.backdropPill`; this box is layout and content only.
  background: none;
}

.action {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border: none;
  border-radius: $radius-pill;
  background: none;
  font-size: $font-size-15;
  font-weight: $font-weight-medium;
  color: var(--text-inverse);
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 150ms ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    color: $color-neutral-600;
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px $color-accent1-400;
  }
}

// A configured schedule tints the pill's label (§8 "Schedule pill highlighted").
.actionActive {
  color: $color-accent1-400;
}

.timerIcon {
  color: $color-accent1-400;
}

// Reserves the gap the overlapping disc sits in, so the two labels don't collide with it.
.discSlot {
  display: block;
  flex: none;
  width: $disc-size;
}

.disc {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  place-items: center;
  width: $disc-size;
  height: $disc-size;
  border-radius: 50%;
  // Fill comes from `.backdropDisc`, which is the half of the silhouette the filter merges.
  background: none;
}

// `--progress` is the swept angle; the remaining arc is transparent so the pill shows through.
.ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    $color-error-700 0deg,
    $color-error-700 var(--progress),
    transparent var(--progress)
  );
  mask-image: radial-gradient(circle closest-side, transparent 88%, #000 89%);
}

// §9 "a 'Turn On Queued' tag is shown" beside the disabled power icon. Same amber colorway and
// dot as the listing card's tag, so one queued command reads identically on both surfaces.
.queuedTag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: $font-size-13;
  font-weight: $font-weight-medium;
  color: $color-warning-900;
}

.queuedDot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $color-warning-900;
}
